import { SelectionOutline } from '@components/Viewer3D/Item/SelectionOutline'
import { shouldSuppressClickAfterDrag } from '@components/Viewer3D/pointerSelectionGuard'
import { SceneObjectRenderer } from '@components/ViewerManager/SceneObjectRendererENC'
import { GIZMO_SCALE_3D, ITEM_SCENE_SCALE } from '@constants'
import { useSharedCubeCamera } from '@context/CubeCameraContext'
import { useViewerContext } from '@context/viewers'
import { useGLBInstance } from '@hooks/useGLBInstance'
import { useItemScale } from '@hooks/useItemScale'
import { useMeshFloorDrag3D } from '@hooks/useMeshFloorDrag3D'
import { PivotControls, Text, useCursor } from '@react-three/drei'
import { ThreeEvent, useThree } from '@react-three/fiber'
import { useAppStore } from '@store'
import { AnyID, Item, LinkedItem } from '@types'
import { calculateDeltasBetweenSelectedItems } from '@utils/calcDeltaItems'
import toQuaternion from '@utils/conversion/toQuaternion'
import { toVector3 } from '@utils/conversion/toVector3'
import { getInitialAngleDegrees } from '@utils/getInitialAngleDegrees'
import { pauseHistoryRecording, resumeHistoryRecording } from '@utils/historyRecording'
import { MODULAR_IKEA_DRAG_ENABLED } from '@utils/modularSnap'
import { useModularUiStore } from '@utils/modularSnap/modularUiStore'
import { collideModularLinkedItems } from '@utils/modularSnap/collideModular'
import {
  hasRinconero166Nearby,
  pouffDragLog,
} from '@utils/sceneObjects/items/pouffDragDiagnostics'
import { resolvePouffFreeDragMatrix } from '@utils/sceneObjects/items/pouffFreeMotion'
import {
  buildItemStrictOBB,
  isWithinPlanMagnetRange,
} from '@utils/modularSnap/straightModuleSnapFootprint'
import {
  isPouff,
  isRinconeroCatalog,
  isStraightModularSnapCatalog,
} from '@utils/sceneObjects/items/rinconeroUtils'
import { isSaneFloorDragPosition } from '@utils/camera/planarFloorDrag'
import { shouldAxesActivate } from '@utils/sceneObjects/items/shouldAxesActivate'
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Group, Matrix3, Matrix4, Quaternion, Vector3 } from 'three'
import { OBB } from 'three/examples/jsm/math/OBB'
import { LinkedItemSon3D } from './LinkedItemSon3D'
type Props = {
  id: Item['id']
  catalogID: Item['catalogID']
  zones: Item['zones']
  morphs: Item['morphs']
  textures: Item['textures']
  matrix: Item['matrix']
  glbSize: Item['glbSize']
}
export const LinkedItem3D = React.memo(function LinkedItem3D({
  id,
  catalogID,
  zones,
  morphs,
  textures,
  matrix,
  glbSize,
}: Props) {
  const select = useAppStore.use.select()
  const editItem = useAppStore.use.editItem()
  const isSelected = useAppStore((store) => store.layers[store.currentLayer].selected.has(id))
  const controlsRef = useRef(undefined)
  const matrixRootRef = useRef<Group>(null)
  const sceneBoundingBoxes = useAppStore.use.sceneBoundingBoxes()
  const prefs = useAppStore.use.prefs()
  const setCurrentObjectID = useAppStore.use.setCurrentObjectID()
  const mode = useAppStore.use.mode()
  const isAlzados = mode === 'MODE_FORCE_ALZADOS' || mode === 'MODE_ALZADOS'

  const item = useAppStore((s) => s.items.get(id)) as LinkedItem
  const morphsFromStore = item?.morphs ?? morphs
  const isBlocked = item?.isBlocked
  const isModalOpen = useAppStore.use.isModalOpen()
  const itemRef = useRef(null)
  const outlineSourceRef = useRef<Group | null>(null)
  const visible = item?.visible
  const controls = useThree((state) => state.controls)
  const setMode = useAppStore.use.setMode()
  const childs = item?.childs as LinkedItem['childs']
  const [hovered, setHovered] = useState(false)
  const ikeaMeshDrag = MODULAR_IKEA_DRAG_ENABLED && !isAlzados
  useCursor(hovered)

  useEffect(() => {
    if (!ikeaMeshDrag || !hovered || !isSelected || isBlocked) return
    document.body.style.cursor = 'grab'
    return () => {
      document.body.style.cursor = ''
    }
  }, [ikeaMeshDrag, hovered, isSelected, isBlocked])
  const currentObjectID = useAppStore.use.currentObjectID()
  const renderMorphs = morphsFromStore
  const glb = useGLBInstance(String(catalogID), id),
    selectedItems = useAppStore((store) => store.layers[store.currentLayer].selected) as Set<AnyID>,
    setDeltas = useAppStore.use.setDeltas(),
    unselectAll = useAppStore.use.unselectAll(),
    updateSelectedObjectsTraslation = useAppStore.use.updateSelectedObjectsTraslation(),
    updateSelectedObjectsPosition = useAppStore.use.updateSelectedObjectsPosition()
  useViewerContext()
  const itemBB = useMemo(() => {
    return new OBB()
  }, [])
  const { sizeRef, scaleVector } = useItemScale(id, morphsFromStore, glbSize)
  const setOBBs = useAppStore.use.setOBBScene()
  const originalModeRef = useRef<string | undefined>(undefined)

  function handleClick(e: ThreeEvent<MouseEvent>) {
    if (isModalOpen) return
    if (shouldSuppressClickAfterDrag()) return
    unselectAll()
    e.stopPropagation()
    select(id, e.nativeEvent.ctrlKey || e.nativeEvent.metaKey)
    setCurrentObjectID(id)
    if (isBlocked) return
  }
  function clearSnappedSidesForDrag() {
    editItem(id, { snappedSides: {} } as any)
    const allItems = useAppStore.getState().items
    allItems.forEach((otherItem, otherId) => {
      if (otherId === id) return
      const ss = (otherItem as any).snappedSides as
        | Partial<Record<'left' | 'right', string>>
        | undefined
      if (!ss) return
      if (ss.left === id || ss.right === id) {
        editItem(
          otherId as any,
          {
            snappedSides: {
              left: ss.left === id ? undefined : ss.left,
              right: ss.right === id ? undefined : ss.right,
            },
          } as any,
        )
      }
    })
  }

  function handleDragStart() {
    if (isModalOpen || isBlocked) return
    setOBBs()
    setCurrentObjectID(id)
    clearSnappedSidesForDrag()
    if (selectedItems.size > 1) {
      const currentItems = useAppStore.getState().items
      setDeltas(calculateDeltasBetweenSelectedItems(selectedItems, currentItems))
      // Solo cambiar el modo si NO estamos en modo alzados
      // En modo alzados, los controles de c?mara ya est?n configurados correctamente
      if (mode !== 'MODE_FORCE_ALZADOS' && mode !== 'MODE_ALZADOS') {
        // Guardar el modo original antes de cambiarlo
        originalModeRef.current = mode
        // Establecer modo de arrastre para desactivar controles de c?mara
        setMode('MODE_DRAGGING_ITEM_3D')
      }
    }
    if (isPouff(catalogID)) {
      lastPouffDragMatrixRef.current = matrix?.clone() ?? null
      lastPouffFingerMatrixRef.current = matrix?.clone() ?? null
      const store = useAppStore.getState()
      pouffDragLog(
        'drag-start',
        {
          puffId: id,
          puffCatalog: catalogID,
          near166: hasRinconero166Nearby(store.items, store.collisionableItems, id),
          collisionableCount: store.collisionableItems.size,
          hint: 'Filtra consola por [PouffDrag]. Desactiva: window.__POUFF_DRAG_DEBUG__ = false',
        },
        { always: true },
      )
    }
  }

  function handleMissed() {
    if (isModalOpen) return
    if (mode === 'MODE_DRAWING_ITEM') {
      return setMode('MODE_3D_VIEW')
    }
  }
  const initialAngleDegrees = getInitialAngleDegrees(item)
  const gizmoColorX = isSelected && currentObjectID === id ? prefs['C/GIZMOX'] : prefs['C/GIZMOZ']
  const gizmoColorZ = isSelected && currentObjectID === id ? prefs['C/GIZMOY'] : prefs['C/GIZMOZ']
  const collisionableItems = useAppStore.use.collisionableItems()
  const collideItems = useAppStore.use.collideItems()
  const collidePouffItems = useAppStore.use.collidePouffItems()
  const addContador = useAppStore.use.addContadorModificaciones()
  const hasCollided = useRef(false)
  const lastPouffDragMatrixRef = useRef<Matrix4 | null>(null)
  const lastPouffFingerMatrixRef = useRef<Matrix4 | null>(null)

  function resetPouffDragRefs() {
    lastPouffDragMatrixRef.current = null
    lastPouffFingerMatrixRef.current = null
  }

  function applyPouffDragMatrix(targetMatrix: Matrix4, restrictedY: number) {
    const pos = new Vector3()
    targetMatrix.decompose(pos, new Quaternion(), new Vector3())
    editItem(id, {
      x: pos.x,
      z: pos.z,
      y: restrictedY,
      matrix: targetMatrix,
    })
    useAppStore.getState().setOBB(id)
    updateSelectedObjectsPosition(id, targetMatrix)
  }

  /** Arrastre libre del puff: solo anti-penetración OBB (sin imán ni snap). */
  function resolvePouffIkeaLiveDrag(proposedMatrix: Matrix4): Matrix4 | null {
    const store = useAppStore.getState()
    const liveItem = store.items.get(id) as Item | undefined
    if (!liveItem) return null

    const itemrot = toQuaternion()
    matrix?.decompose(toVector3(), itemrot, toVector3())
    const proposedPos = new Vector3()
    proposedMatrix.decompose(proposedPos, new Quaternion(), new Vector3())
    if (!isSaneFloorDragPosition(proposedPos)) return null

    let restrictedY = proposedPos.y
    if (mode === 'MODE_3D_VIEW') {
      restrictedY = Math.max(0, restrictedY)
    }

    const proposed = new Matrix4()
      .makeRotationFromQuaternion(itemrot)
      .setPosition(proposedPos.x, restrictedY, proposedPos.z)

    const previousFree =
      lastPouffDragMatrixRef.current ?? matrix?.clone() ?? liveItem.matrix?.clone() ?? null

    const resolved = resolvePouffFreeDragMatrix(
      proposed,
      previousFree,
      liveItem,
      store.items,
      store.collisionableItems,
      id,
    )
    if (!resolved) return null

    lastPouffDragMatrixRef.current = resolved.clone()
    lastPouffFingerMatrixRef.current = proposed.clone()
    return resolved
  }

  function applyFreeFloorDragPosition(itemPosition: Vector3, itemrot: Quaternion) {
    let restrictedY = itemPosition.y
    if (mode === 'MODE_3D_VIEW') {
      restrictedY = Math.max(0, restrictedY)
    }

    const restrictedMatrix = new Matrix4()
      .makeRotationFromQuaternion(itemrot)
      .setPosition(itemPosition.x, restrictedY, itemPosition.z)

    editItem(id, {
      x: itemPosition.x,
      z: itemPosition.z,
      y: restrictedY,
      matrix: restrictedMatrix,
    })
    updateSelectedObjectsPosition(id, restrictedMatrix)
  }

  function trySnapModularOnDragEnd() {
    if (!ikeaMeshDrag || isPouff(catalogID)) return

    setOBBs()
    useAppStore.getState().setOBB(id)

    const store = useAppStore.getState()
    const items = store.items
    const draggedItem = items.get(id)
    if (!draggedItem?.matrix || !collisionableItems.has(id)) return

    const itemPosition = new Vector3()
    const itemrot = new Quaternion()
    draggedItem.matrix.decompose(itemPosition, itemrot, new Vector3())

    // Imán: OBB × 1.5 con rotación actual + alcance en planta vs 166 (pata↔lateral).
    const itemCenter = toVector3([
      draggedItem.width / 2,
      draggedItem.height / 2,
      draggedItem.thickness / 2,
    ])
    itemCenter.applyQuaternion(itemrot).add(itemPosition)
    itemBB.center.copy(itemCenter)
    itemBB.halfSize.set(draggedItem.width / 2, draggedItem.height / 2, draggedItem.thickness / 2)
    itemBB.halfSize.multiply(toVector3([1.5, 1.5, 1.5]))
    itemBB.rotation.copy(
      new Matrix3().setFromMatrix4(new Matrix4().makeRotationFromQuaternion(itemrot)),
    )

    const collisionableItemsWithDistance: Array<{ id: AnyID; distance2D: number }> = []

    collisionableItems.forEach((collisionableItem) => {
      if (collisionableItem === id) return
      const collisionOBB = sceneBoundingBoxes.get(collisionableItem)
      const otherItem = items.get(collisionableItem)
      if (!collisionOBB || !otherItem) return

      const otherItemPos = new Vector3()
      otherItem.matrix.decompose(otherItemPos, new Quaternion(), new Vector3())
      const distance2D = Math.hypot(
        itemPosition.x - otherItemPos.x,
        itemPosition.z - otherItemPos.z,
      )
      collisionableItemsWithDistance.push({ id: collisionableItem, distance2D })
    })

    collisionableItemsWithDistance.sort((a, b) => a.distance2D - b.distance2D)

    const dragIsStraight = isStraightModularSnapCatalog(catalogID)

    for (const { id: collisionableItem } of collisionableItemsWithDistance) {
      const collisionOBB = sceneBoundingBoxes.get(collisionableItem)
      const otherItem = items.get(collisionableItem)
      if (!collisionOBB || !otherItem) continue

      const otherPos = new Vector3()
      otherItem.matrix?.decompose(otherPos, new Quaternion(), new Vector3())
      const intersectsOBB = itemBB.intersectsOBB(collisionOBB.boundingBox)
      const intersectsPlan =
        dragIsStraight &&
        isRinconeroCatalog(otherItem.catalogID) &&
        isWithinPlanMagnetRange(
          itemPosition,
          Number(draggedItem.width || 0),
          Number(draggedItem.thickness || 0),
          otherPos,
          Number(otherItem.width || 0),
          Number(otherItem.thickness || 0),
        )
      if (!intersectsOBB && !intersectsPlan) continue

      const snapAttempt = collideModularLinkedItems(
        id,
        collisionableItem,
        (oid) => items.get(oid)?.catalogID,
        collideItems,
        collidePouffItems,
      )
      if (snapAttempt?.snapped) {
        hasCollided.current = true
        break
      }
    }
  }

  function resolveModularTranslationDrag(
    proposedMatrix: Matrix4,
    itemrot: Quaternion,
    options?: { visualOnlyFreeMove?: boolean },
  ): Matrix4 | null {
    const items = useAppStore.getState().items
    const itemPosition = new Vector3()
    proposedMatrix.decompose(itemPosition, new Quaternion(), new Vector3())
    if (!isSaneFloorDragPosition(itemPosition)) return null

    // Imán: OBB × 1.5 + rotación; vs 166 también alcance en planta (160 = RELAX).
    const itemCenter = toVector3([item.width / 2, item.height / 2, item.thickness / 2])
    itemCenter.applyQuaternion(itemrot).add(itemPosition)
    itemBB.center.copy(itemCenter)
    itemBB.halfSize.set(item.width / 2, item.height / 2, item.thickness / 2)
    itemBB.halfSize.multiply(toVector3([1.5, 1.5, 1.5]))
    itemBB.rotation.copy(
      new Matrix3().setFromMatrix4(new Matrix4().makeRotationFromQuaternion(itemrot)),
    )
    let hasCollision = false

    if (collisionableItems.has(id)) {
      const collisionableItemsWithDistance: Array<{ id: AnyID; distance2D: number }> = []

      collisionableItems.forEach((collisionableItem) => {
        if (collisionableItem === id) return
        const collisionOBB = sceneBoundingBoxes.get(collisionableItem)
        const otherItem = items.get(collisionableItem)
        if (!collisionOBB || !otherItem) return

        const otherItemPos = new Vector3()
        otherItem.matrix.decompose(otherItemPos, new Quaternion(), new Vector3())
        const distance2D = Math.hypot(
          itemPosition.x - otherItemPos.x,
          itemPosition.z - otherItemPos.z,
        )
        collisionableItemsWithDistance.push({ id: collisionableItem, distance2D })
      })

      collisionableItemsWithDistance.sort((a, b) => a.distance2D - b.distance2D)

      for (const { id: collisionableItem } of collisionableItemsWithDistance) {
        if (hasCollision) break

        const collisionOBB = sceneBoundingBoxes.get(collisionableItem)
        const otherItem = items.get(collisionableItem)
        if (!collisionOBB || !otherItem) continue

        const otherPos = new Vector3()
        otherItem.matrix.decompose(otherPos, new Quaternion(), new Vector3())
        const intersectsOBB = itemBB.intersectsOBB(collisionOBB.boundingBox)
        const intersectsPlan =
          isRinconeroCatalog(otherItem.catalogID) &&
          isStraightModularSnapCatalog(catalogID) &&
          isWithinPlanMagnetRange(
            itemPosition,
            Number(item.width || 0),
            Number(item.thickness || 0),
            otherPos,
            Number(otherItem.width || 0),
            Number(otherItem.thickness || 0),
          )
        const intersects = intersectsOBB || intersectsPlan

        if (!intersects) continue

        const snapAttempt = collideModularLinkedItems(
          id,
          collisionableItem,
          (oid) => items.get(oid)?.catalogID,
          collideItems,
          collidePouffItems,
          {
            proposedPosition: {
              x: itemPosition.x,
              y: itemPosition.y,
              z: itemPosition.z,
            },
          },
        )
        if (snapAttempt?.snapped) {
          hasCollision = true
          hasCollided.current = true
          break
        }
      }
    }

    if (hasCollision) {
      const snappedMatrix = useAppStore.getState().items.get(id)?.matrix as Matrix4 | undefined
      return snappedMatrix?.clone() ?? null
    }

    // Anti-penetración siempre (también en drag en vivo): no entrar dentro de otro módulo.
    const strictBB = buildItemStrictOBB(proposedMatrix, item)
    if (collisionableItems.has(id)) {
      for (const otherId of collisionableItems) {
        if (otherId === id) continue
        const otherItem = items.get(otherId)
        if (!otherItem?.matrix) continue
        if (otherItem.type !== 'linkedItem') continue
        if (/^BR\d+/i.test(String(otherItem.catalogID || ''))) continue
        const otherBB = buildItemStrictOBB(otherItem.matrix, otherItem)
        if (strictBB.intersectsOBB(otherBB)) {
          return null
        }
      }
    }

    let restrictedY = itemPosition.y
    if (mode === 'MODE_3D_VIEW') {
      restrictedY = Math.max(0, restrictedY)
    }

    const restrictedMatrix = new Matrix4()
      .makeRotationFromQuaternion(itemrot)
      .setPosition(itemPosition.x, restrictedY, itemPosition.z)

    if (!options?.visualOnlyFreeMove) {
      editItem(id, {
        x: itemPosition.x,
        z: itemPosition.z,
        y: restrictedY,
        matrix: restrictedMatrix,
      })
      updateSelectedObjectsPosition(id, restrictedMatrix)
    }

    return restrictedMatrix
  }

  function handleIkeaDragLive(proposedMatrix: Matrix4): Matrix4 | null {
    if (isPouff(catalogID)) {
      return resolvePouffIkeaLiveDrag(proposedMatrix)
    }
    const itemrot = toQuaternion()
    matrix?.decompose(toVector3(), itemrot, toVector3())
    // Solo aplaza editItem; la anti-penetración y el snap siguen activos.
    return resolveModularTranslationDrag(proposedMatrix, itemrot, { visualOnlyFreeMove: true })
  }

  function handleIkeaDragCommit(l: Matrix4) {
    if (isModalOpen || isBlocked) return
    if (!itemRef.current) return

    const itemPosition = new Vector3()
    const itemrot = toQuaternion()
    matrix?.decompose(toVector3(), itemrot, toVector3())
    l.decompose(itemPosition, new Quaternion(), new Vector3())
    if (!isSaneFloorDragPosition(itemPosition)) return

    if (isPouff(catalogID)) {
      const resolved = resolvePouffIkeaLiveDrag(l)
      if (resolved) {
        const y = mode === 'MODE_3D_VIEW' ? Math.max(0, itemPosition.y) : itemPosition.y
        applyPouffDragMatrix(resolved, y)
      }
      useAppStore.getState().setOBB(id)
      return
    }

    applyFreeFloorDragPosition(itemPosition, itemrot)
    useAppStore.getState().setOBB(id)
  }

  function handleDrag<T extends Matrix4>(l: Matrix4, dl: T, w: T) {
    const activeObjectID = useAppStore.getState().currentObjectID
    if (isModalOpen || isBlocked || activeObjectID !== id) return
    if (!itemRef.current) return
    const items = useAppStore.getState().items
    if (controlsRef.current === 'Rotator') {
      setDeltas(calculateDeltasBetweenSelectedItems(selectedItems, items))
      editItem(id, { matrix: w.clone() })
      // console.log(initialAngleDegrees, w.clone())
      if (selectedItems.size > 1) {
        updateSelectedObjectsTraslation(id, l, initialAngleDegrees)
      }
    } else {
      const itemPosition = toVector3()
      const itemrot = toQuaternion()
      matrix?.decompose(toVector3(), itemrot, toVector3())
      l.decompose(itemPosition, new Quaternion(), new Vector3())
      if (!isSaneFloorDragPosition(itemPosition)) return

      const lr = new Matrix4()
        .makeRotationFromQuaternion(itemrot)
        .setPosition(itemPosition.x, itemPosition.y, itemPosition.z)

      if (ikeaMeshDrag) {
        applyFreeFloorDragPosition(itemPosition, itemrot)
        return
      }

      const draggingIsPouff = isPouff(catalogID)
      if (draggingIsPouff) {
        let restrictedY = itemPosition.y
        if (mode === 'MODE_3D_VIEW') {
          restrictedY = Math.max(0, restrictedY)
        }

        const proposedMatrix = lr.clone().setPosition(itemPosition.x, restrictedY, itemPosition.z)
        const previousFree =
          lastPouffDragMatrixRef.current ?? matrix?.clone() ?? proposedMatrix.clone()
        const resolvedMatrix = resolvePouffFreeDragMatrix(
          proposedMatrix,
          previousFree,
          item,
          items,
          collisionableItems,
          id,
        )
        if (!resolvedMatrix) return

        lastPouffFingerMatrixRef.current = proposedMatrix.clone()
        lastPouffDragMatrixRef.current = resolvedMatrix.clone()
        applyPouffDragMatrix(resolvedMatrix, restrictedY)
        return
      }

      resolveModularTranslationDrag(lr, itemrot)
    }
  }
  // const items = useAppStore((s) => s.items)
  const textoManoIZQ = useAppStore((s) => s.prefs.MANOIZQUIERDA)
  const textoManoDCH = useAppStore((s) => s.prefs.MANODERECHA)
  const colorTextoMano = useAppStore((s) => s.prefs.COLORTEXTOBISAGRA)
  const verBisagras = useAppStore((s) => s.prefs.VERBISAGRAALZADO)
  const showText = verBisagras && mode === 'MODE_FORCE_ALZADOS' && item?.bisagra
  const isGolaItem = typeof catalogID === 'string' && catalogID.toLowerCase().includes('g')

  const getGolaText = () => {
    if (!isGolaItem || item?.hinge === undefined) return ''
    return item.hinge === false ? textoManoDCH : textoManoIZQ
  }

  const textXPosition = (0.3 * item?.width) / 600
  const textYPosition = 0.84

  // Usar el renderTarget del contexto CubeCamera
  const { renderTarget } = useSharedCubeCamera() || {}

  const { meshPointerHandlers, suppressClick } = useMeshFloorDrag3D({
    enabled: ikeaMeshDrag,
    id,
    matrix,
    isBlocked,
    isModalOpen,
    isSelected,
    matrixRootRef,
    onPointerDownSelect: (e) => {
      select(id, e.nativeEvent.ctrlKey || e.nativeEvent.metaKey)
      setCurrentObjectID(id)
    },
    onDragStart: () => {
      controlsRef.current = undefined
      pauseHistoryRecording()
      handleDragStart()
      const store = useAppStore.getState()
      if (
        store.layers[store.currentLayer].selected.size <= 1 &&
        store.mode !== 'MODE_FORCE_ALZADOS' &&
        store.mode !== 'MODE_ALZADOS'
      ) {
        originalModeRef.current = store.mode
        setMode('MODE_DRAGGING_ITEM_3D')
      }
    },
    onDragLive: handleIkeaDragLive,
    onDragCommit: handleIkeaDragCommit,
    onDragEnd: () => {
      // Puff: sin snap al soltar; la pose final es la del arrastre + colisión.
      if (!isPouff(catalogID) && !hasCollided.current) {
        trySnapModularOnDragEnd()
      }
      if (isPouff(catalogID)) {
        useAppStore.getState().setOBB(id)
      }
      resetPouffDragRefs()
      resumeHistoryRecording()
      controlsRef.current = undefined
      const currentMode = useAppStore.getState().mode
      if (currentMode === 'MODE_DRAGGING_ITEM_3D' && originalModeRef.current) {
        setMode(originalModeRef.current)
        originalModeRef.current = undefined
      }
      if (hasCollided.current && item.tipoNormalizado === 'MBJ') {
        addContador()
        hasCollided.current = false
      }
    },
  })

  useLayoutEffect(() => {
    if (!ikeaMeshDrag || !matrixRootRef.current || !matrix) return
    const ui = useModularUiStore.getState()
    if (ui.itemDragActive && String(ui.draggingItemId) === String(id)) return
    matrixRootRef.current.matrix.copy(matrix)
    matrixRootRef.current.matrixAutoUpdate = false
    matrixRootRef.current.updateMatrixWorld(true)
  }, [ikeaMeshDrag, matrix, id])

  const itemContent = (
    <group
      key={item?.childrenOrder ? item.childrenOrder.join('-') : `${id}-default-key`}
      ref={(node) => {
        outlineSourceRef.current = node
        itemRef.current = node
      }}
      name={id}
      scale={ITEM_SCENE_SCALE}
      onPointerMissed={handleMissed}
      onPointerOver={(e) => (e.stopPropagation(), setHovered(true))}
      onPointerOut={(e) => setHovered(false)}
      onClick={(e) => {
        if (suppressClick()) return
        handleClick(e)
      }}
      onDoubleClick={() => {
        controls &&
          controls?.fitToBox(itemRef.current, true, { paddingLeft: 0.5, paddingRight: 0.5 })
      }}
      visible={visible}
    >
      <group
        ref={sizeRef}
        scale={scaleVector}
        {...(ikeaMeshDrag ? meshPointerHandlers : {})}
      >
        <SceneObjectRenderer
          key={glb?.uuid}
          id={id}
          object={glb}
          zones={zones}
          morphs={renderMorphs}
          textures={textures}
          renderTarget={renderTarget}
          paginaPdf={item.paginaPdf}
          itemHeight={item.height}
          itemWidth={item.width}
          itemThickness={item.thickness}
          getContiguousItems={useAppStore.use.getContItemsForEncimeras()}
          fatherId={id}
        />
      </group>
      {showText && (
        <Text
          position={[textXPosition, textYPosition, 0.99]}
          fontSize={0.05}
          color={colorTextoMano}
          anchorX='center'
          anchorY='middle'
        >
          {getGolaText()}
        </Text>
      )}
      {childs &&
        visible &&
        childs.size > 0 &&
        Array.from(childs).map((childId) => (
          <LinkedItemSon3D
            id={childId}
            key={childId}
            renderTarget={renderTarget}
            itemWidth={item.width}
            fatherId={id}
            matrix={matrix}
          />
        ))}
      <SelectionOutline
        id={id}
        width={item.width}
        height={item.height}
        thickness={item.thickness}
        parentGroupRef={outlineSourceRef}
        forceVisible={isSelected && !isBlocked}
        includeAllMeshes
        meshRevision={`${glb?.uuid ?? 'pending'}-${item?.childrenOrder?.join(',') ?? ''}`}
      />
    </group>
  )

  if (ikeaMeshDrag) {
    return (
      <group
        ref={matrixRootRef}
        matrixAutoUpdate={false}
      >
        {itemContent}
      </group>
    )
  }

  return (
    <PivotControls
      lineWidth={3}
      ref={itemRef}
      // rotation={ITEMS_REVERT_FIXED_ROTATION.toArray()}
      autoTransform={false}
      key={`${id}-${textures}`}
      scale={GIZMO_SCALE_3D}
      visible={visible && mode !== 'MODE_DRAGGING_HOLE'}
      matrix={matrix}
      onDrag={handleDrag}
      onDragStart={(e) => {
        controlsRef.current = e.component
        pauseHistoryRecording()
        handleDragStart()
      }}
      onDragEnd={() => {
        // Puff: sin snap al soltar.
        if (isPouff(catalogID)) {
          useAppStore.getState().setOBB(id)
        }
        resetPouffDragRefs()
        resumeHistoryRecording()
        controlsRef.current = undefined
        // Restaurar el modo original cuando termina el arrastre
        // Solo si cambiamos el modo (no en modo alzados)
        if (mode === 'MODE_DRAGGING_ITEM_3D' && originalModeRef.current) {
          setMode(originalModeRef.current)
          originalModeRef.current = undefined
        }
        if (hasCollided.current) {
          addContador()
          hasCollided.current = false // Resetear para el pr?ximo drag
        }
      }}
      activeAxes={shouldAxesActivate(isSelected && !isBlocked, mode)}
      rotationLimits={[[0, 0], undefined, [0, 0]]}
      axisColors={[gizmoColorX, prefs['C/GIZMOZ'], gizmoColorZ]}
      depthTest={false}
      disableScaling
      userData={{ pivotControlsVisible: true }}
    >
      {itemContent}
    </PivotControls>
  )
})
